import { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../hooks'
import { fetchRuns, fetchMoreRuns, createRun, deleteRun } from '../slices/runsSlice'
import SidebarNavigation from '../components/SidebarNavigation'
import RunsTable from '../components/RunsTable'
import NewRunDialog from '../components/NewRunDialog'
import './RunsPage.css'

function RunsPage() {
  const dispatch = useAppDispatch()
  const { items: runs, loading, error, hasMore, page, total } = useAppSelector((state) => state.runs)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const pageStickyHeaderRef = useRef<HTMLElement>(null)
  const [runsTableStickyTop, setRunsTableStickyTop] = useState(0)

  useLayoutEffect(() => {
    const el = pageStickyHeaderRef.current
    if (!el) return
    const update = () => setRunsTableStickyTop(el.offsetHeight)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    dispatch(fetchRuns())
  }, [dispatch])

  const handleLoadMore = useCallback(async () => {
    setLoadingMore(true)
    await dispatch(fetchMoreRuns(page + 1))
    setLoadingMore(false)
  }, [dispatch, page])

  const handleCreateRun = useCallback(
    async (data: {
      agent_ids: string[]
      selected_tags: string[]
      selected_failures: string[]
      attempts_count: number
      refined: boolean
    }) => {
      await dispatch(createRun(data)).unwrap()
    },
    [dispatch]
  )

  const handleDeleteRun = useCallback(
    (id: string) => {
      dispatch(deleteRun(id))
    },
    [dispatch]
  )

  return (
    <div data-testid="runs-page" className="runs-page">
      <SidebarNavigation />

      <main
        className="app-page-main"
        style={{
          ['--runs-table-sticky-top' as string]: `${runsTableStickyTop > 0 ? runsTableStickyTop : 72}px`,
        }}
      >
        <header ref={pageStickyHeaderRef} className="app-page-sticky-header">
          <div className="app-page-sticky-header-inner app-page-sticky-header-inner--row">
            <h1 className="app-page-title runs-title">Benchmark Runs</h1>
            <button
              data-testid="new-run-btn"
              className="new-run-btn"
              onClick={() => setDialogOpen(true)}
            >
              + New Run
            </button>
          </div>
        </header>

        {loading && (
          <div data-testid="runs-loading" className="runs-loading">
            Loading runs...
          </div>
        )}

        {error && (
          <div data-testid="runs-error" className="runs-error">
            {error}
          </div>
        )}

        {!loading && !error && (
          <RunsTable runs={runs} onDelete={handleDeleteRun} />
        )}

        {hasMore && !loading && (
          <div className="app-load-more-wrap">
            <button
              data-testid="load-more-runs"
              type="button"
              className="app-load-more-btn load-more-btn"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading...' : `Load More (${runs.length} of ${total})`}
            </button>
          </div>
        )}
      </main>

      <NewRunDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleCreateRun}
      />
    </div>
  )
}

export default RunsPage
